import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiChevronDown } = FiIcons;

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [colorIndex, setColorIndex] = useState(0);
  const location = useLocation();

  // Check if current page is Quinformation
  const isQuinformationPage = location.pathname === '/quinformation';

  // Define the color scheme for animation - only for Quinformation page
  const colors = {
    orange: '#FF6A00',
    blue: '#00F0FF',
    purple: '#D94EFF'
  };

  // Animated color effect - only for Quinformation page
  useEffect(() => {
    if (isQuinformationPage) {
      const interval = setInterval(() => {
        setColorIndex(prev => (prev + 1) % 100);
      }, 60); // Update frequently for smooth transitions
      return () => clearInterval(interval);
    }
  }, [isQuinformationPage]);

  // Interpolate between colors for smooth transitions
  const getInterpolatedColor = () => {
    // Create smooth transitions between the three colors
    if (colorIndex < 33) {
      // Transition from orange to blue
      const ratio = colorIndex / 33;
      return interpolateColor(colors.orange, colors.blue, ratio);
    } else if (colorIndex < 66) {
      // Transition from blue to purple
      const ratio = (colorIndex - 33) / 33;
      return interpolateColor(colors.blue, colors.purple, ratio);
    } else {
      // Transition from purple to orange
      const ratio = (colorIndex - 66) / 34;
      return interpolateColor(colors.purple, colors.orange, ratio);
    }
  };

  // Helper function to interpolate between two colors
  const interpolateColor = (color1, color2, ratio) => {
    const hex = (x) => {
      const hex = Math.round(x).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    const r1 = parseInt(color1.substring(1, 3), 16);
    const g1 = parseInt(color1.substring(3, 5), 16);
    const b1 = parseInt(color1.substring(5, 7), 16);

    const r2 = parseInt(color2.substring(1, 3), 16);
    const g2 = parseInt(color2.substring(3, 5), 16);
    const b2 = parseInt(color2.substring(5, 7), 16);

    const r = r1 + ratio * (r2 - r1);
    const g = g1 + ratio * (g2 - g1);
    const b = b1 + ratio * (b2 - b1);

    return `#${hex(r)}${hex(g)}${hex(b)}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    {
      name: 'Solutions',
      href: '/solutions'
    },
    {
      name: 'About Us',
      href: '/about',
      dropdown: [
        {
          name: 'Our Team',
          href: '/about/team'
        },
        {
          name: 'Contact Us',
          href: '/about/contact'
        },
      ]
    },
  ];

  const handleDropdownToggle = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  // Get the current animated color for Quinformation page
  const animatedColor = isQuinformationPage ? getInterpolatedColor() : '#FF6B00';
  const animatedGradient = isQuinformationPage 
    ? `linear-gradient(to right, ${colors.purple}, ${animatedColor}, ${colors.blue})`
    : 'linear-gradient(135deg, #FF6B00 0%, #FFB366 100%)';

  // Dynamic style for navigation based on page
  const navStyle = isQuinformationPage ? {
    borderColor: animatedColor,
    boxShadow: `0 4px 12px ${animatedColor}20`
  } : {};

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/80 backdrop-blur-md border-b border-gray-800' : 'bg-transparent'
      }`}
      style={isScrolled && isQuinformationPage ? navStyle : {}}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-3"
          >
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="https://cqpenfycgbztkozuwfiu.supabase.co/storage/v1/object/sign/logos/icononly_transparent_nobuffer.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZjlmMTQ4My1mMmM0LTRjZTAtOWIxNi01N2E0Njk3ZWIxYjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsb2dvcy9pY29ub25seV90cmFuc3BhcmVudF9ub2J1ZmZlci5wbmciLCJpYXQiOjE3NTQ5MTY2MDUsImV4cCI6MzMyOTA5MTY2MDV9.92E3oR6MA9SmcZKdeU0lsdO-y2OGtrsHF2YwxSCzQDQ" 
                  alt="Quantum Leverage Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-bold">Quantum Leverage</span>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <div key={item.name} className="relative group">
                {item.dropdown ? (
                  <div className="flex items-center">
                    <button
                      onClick={() => handleDropdownToggle(index)}
                      className={`text-gray-300 hover:text-${isQuinformationPage ? 'white' : 'primary-orange'} transition-colors duration-200 font-medium flex items-center`}
                      style={isQuinformationPage ? { color: activeDropdown === index ? animatedColor : 'rgb(209, 213, 219)' } : {}}
                    >
                      {item.name}
                      <SafeIcon
                        icon={FiChevronDown}
                        className={`ml-1 w-4 h-4 transition-transform duration-300 ${activeDropdown === index ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {/* Dropdown Menu */}
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{
                        opacity: activeDropdown === index ? 1 : 0,
                        y: activeDropdown === index ? 0 : -10,
                        pointerEvents: activeDropdown === index ? 'auto' : 'none'
                      }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-48 bg-gray-900 border border-gray-800 shadow-xl z-50"
                      style={isQuinformationPage ? { borderColor: animatedColor } : {}}
                    >
                      {item.dropdown.map((dropItem) => (
                        <Link
                          key={dropItem.name}
                          to={dropItem.href}
                          className={`block px-4 py-3 text-gray-300 hover:bg-gray-800 hover:text-${isQuinformationPage ? 'white' : 'primary-orange'} transition-colors`}
                          onClick={() => setActiveDropdown(null)}
                          style={isQuinformationPage ? { ':hover': { color: animatedColor } } : {}}
                        >
                          {dropItem.name}
                        </Link>
                      ))}
                    </motion.div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`text-gray-300 hover:text-${isQuinformationPage ? 'white' : 'primary-orange'} transition-colors duration-200 font-medium`}
                    style={isQuinformationPage ? { ':hover': { color: animatedColor } } : {}}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <Link to="/quinformation">
              <motion.span
                whileHover={{ scale: 1.05 }}
                className="text-gray-300 hover:text-primary-orange transition-colors duration-200 font-medium relative"
                style={isQuinformationPage ? { color: animatedColor } : {}}
              >
                Quinformation
                <span
                  className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-primary-orange text-black text-[10px] font-bold rounded-sm"
                  style={isQuinformationPage ? { background: animatedColor } : {}}
                >
                  SOON
                </span>
              </motion.span>
            </Link>
            <Link to="/booking">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-black px-6 py-2 font-semibold transition-colors duration-200"
                style={{
                  background: isQuinformationPage ? animatedColor : '#FF6B00',
                  boxShadow: isQuinformationPage ? `0 0 15px ${animatedColor}40` : 'none'
                }}
              >
                Book Demo
              </motion.button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2"
            style={isQuinformationPage ? { color: animatedColor } : {}}
          >
            <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-gray-950 border-t border-gray-800 py-4"
            style={isQuinformationPage ? { borderColor: animatedColor } : {}}
          >
            {navItems.map((item, index) => (
              <div key={item.name}>
                {item.dropdown ? (
                  <div>
                    <button
                      onClick={() => handleDropdownToggle(index)}
                      className={`flex items-center justify-between w-full px-4 py-3 text-gray-300 hover:text-${isQuinformationPage ? 'white' : 'primary-orange'} transition-colors`}
                      style={isQuinformationPage ? { ':hover': { color: animatedColor } } : {}}
                    >
                      {item.name}
                      <SafeIcon
                        icon={FiChevronDown}
                        className={`ml-1 w-4 h-4 transition-transform duration-300 ${activeDropdown === index ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {/* Mobile Dropdown */}
                    {activeDropdown === index && (
                      <div
                        className="bg-gray-900 pl-8"
                        style={isQuinformationPage ? { borderLeft: `2px solid ${animatedColor}` } : {}}
                      >
                        {item.dropdown.map((dropItem) => (
                          <Link
                            key={dropItem.name}
                            to={dropItem.href}
                            className={`block px-4 py-3 text-gray-400 hover:text-${isQuinformationPage ? 'white' : 'primary-orange'} transition-colors`}
                            onClick={() => setIsMobileMenuOpen(false)}
                            style={isQuinformationPage ? { ':hover': { color: animatedColor } } : {}}
                          >
                            {dropItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`block px-4 py-3 text-gray-300 hover:text-${isQuinformationPage ? 'white' : 'primary-orange'} transition-colors`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={isQuinformationPage ? { ':hover': { color: animatedColor } } : {}}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <Link
              to="/quinformation"
              className="flex items-center justify-between px-4 py-3 text-gray-300 hover:text-primary-orange transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
              style={isQuinformationPage ? { color: animatedColor } : {}}
            >
              <span>Quinformation</span>
              <span
                className="px-1.5 py-0.5 bg-primary-orange text-black text-[10px] font-bold rounded-sm"
                style={isQuinformationPage ? { background: animatedColor } : {}}
              >
                SOON
              </span>
            </Link>
            <div className="px-4 pt-2">
              <Link to="/booking">
                <button
                  className="w-full text-black py-3 font-semibold transition-colors"
                  style={{
                    background: isQuinformationPage ? animatedColor : '#FF6B00',
                    boxShadow: isQuinformationPage ? `0 0 10px ${animatedColor}40` : 'none'
                  }}
                >
                  Book Demo
                </button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navigation;